import React, { useState, useEffect } from 'react';
import { Send, Plus, Trash2, Copy, Download, Upload, Clock, CheckCircle, XCircle, Info } from 'lucide-react';

const ApiDev = () => {
  const [request, setRequest] = useState({
    method: 'GET',
    url: 'https://jsonplaceholder.typicode.com/posts/1',
    headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
    params: [],
    body: '',
    bodyType: 'json'
  });

  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('headers');
  const [history, setHistory] = useState([]);
  const [savedRequests, setSavedRequests] = useState([]);

  const methods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
  const bodyTypes = ['json', 'text', 'form-data', 'x-www-form-urlencoded'];

  // Add new header/param
  const addKeyValue = (type) => {
    setRequest(prev => ({
      ...prev,
      [type]: [...prev[type], { key: '', value: '', enabled: true }]
    }));
  };

  // Update header/param
  const updateKeyValue = (type, index, field, value) => {
    setRequest(prev => ({
      ...prev,
      [type]: prev[type].map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Remove header/param
  const removeKeyValue = (type, index) => {
    setRequest(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  // Build final URL with params
  const buildUrl = () => {
    const enabledParams = request.params.filter(p => p.enabled && p.key);
    if (enabledParams.length === 0) return request.url;
    
    const params = new URLSearchParams();
    enabledParams.forEach(p => params.append(p.key, p.value));
    return `${request.url}?${params.toString()}`;
  };

  // Send request
  const sendRequest = async () => {
    setLoading(true);
    const startTime = Date.now();
    
    try {
      const enabledHeaders = request.headers.filter(h => h.enabled && h.key);
      const headers = {};
      enabledHeaders.forEach(h => headers[h.key] = h.value);

      const config = {
        method: request.method,
        headers,
      };

      // Add body for non-GET requests
      if (request.method !== 'GET' && request.method !== 'HEAD' && request.body) {
        if (request.bodyType === 'json') {
          config.body = request.body;
          headers['Content-Type'] = headers['Content-Type'] || 'application/json';
        } else if (request.bodyType === 'text') {
          config.body = request.body;
        } else if (request.bodyType === 'x-www-form-urlencoded') {
          config.body = request.body;
          headers['Content-Type'] = 'application/x-www-form-urlencoded';
        }
      }

      const response = await fetch(buildUrl(), config);
      const endTime = Date.now();
      
      let responseBody;
      const contentType = response.headers.get('content-type') || '';
      
      if (contentType.includes('application/json')) {
        responseBody = await response.json();
      } else {
        responseBody = await response.text();
      }

      const responseData = {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: responseBody,
        time: endTime - startTime,
        size: JSON.stringify(responseBody).length,
        timestamp: new Date().toISOString()
      };

      setResponse(responseData);
      
      // Add to history
      setHistory(prev => [{
        id: Date.now(),
        method: request.method,
        url: buildUrl(),
        status: response.status,
        time: responseData.time,
        timestamp: responseData.timestamp
      }, ...prev.slice(0, 9)]);

    } catch (error) {
      setResponse({
        error: error.message,
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  // Save request
  const saveRequest = () => {
    const saved = {
      id: Date.now(),
      name: `${request.method} ${request.url}`,
      request: { ...request },
      timestamp: new Date().toISOString()
    };
    setSavedRequests(prev => [saved, ...prev]);
  };

  // Load saved request
  const loadRequest = (savedReq) => {
    setRequest(savedReq.request);
    setResponse(null);
  };

  // Copy response
  const copyResponse = () => {
    navigator.clipboard.writeText(JSON.stringify(response, null, 2));
  };

  // KeyValue component for headers/params
  const KeyValueEditor = ({ type, title }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-700">{title}</h3>
        <button
          onClick={() => addKeyValue(type)}
          className="flex items-center gap-1 px-2 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          <Plus size={14} /> Add
        </button>
      </div>
      
      {request[type].map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={item.enabled}
            onChange={(e) => updateKeyValue(type, index, 'enabled', e.target.checked)}
            className="w-4 h-4"
          />
          <input
            type="text"
            placeholder="Key"
            value={item.key}
            onChange={(e) => updateKeyValue(type, index, 'key', e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Value"
            value={item.value}
            onChange={(e) => updateKeyValue(type, index, 'value', e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={() => removeKeyValue(type, index)}
            className="p-2 text-red-500 hover:bg-red-50 rounded"
          >
            <Trash2 size={16} />
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <h1 className="text-3xl font-bold mb-2">API Development</h1>
          <p className="text-blue-100">Test any HTTP API endpoint in real-time</p>
        </div>

        <div className="flex">
          {/* Sidebar */}
          <div className="w-80 bg-gray-50 border-r border-gray-200 p-4">
            {/* Quick Actions */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={saveRequest}
                  className="w-full flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  <Download size={16} /> Save Request
                </button>
                <button
                  onClick={() => setRequest({
                    method: 'GET',
                    url: '',
                    headers: [{ key: 'Content-Type', value: 'application/json', enabled: true }],
                    params: [],
                    body: '',
                    bodyType: 'json'
                  })}
                  className="w-full px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  New Request
                </button>
              </div>
            </div>

            {/* Saved Requests */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-700 mb-3">Saved Requests</h3>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {savedRequests.map(saved => (
                  <button
                    key={saved.id}
                    onClick={() => loadRequest(saved)}
                    className="w-full text-left px-3 py-2 text-sm bg-white border border-gray-200 rounded hover:bg-gray-50 truncate"
                  >
                    {saved.name}
                  </button>
                ))}
              </div>
            </div>

            {/* History */}
            <div>
              <h3 className="font-semibold text-gray-700 mb-3">Recent History</h3>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {history.map(item => (
                  <div key={item.id} className="bg-white border border-gray-200 rounded p-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.method === 'GET' ? 'bg-green-100 text-green-800' :
                        item.method === 'POST' ? 'bg-blue-100 text-blue-800' :
                        item.method === 'PUT' ? 'bg-yellow-100 text-yellow-800' :
                        item.method === 'DELETE' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.method}
                      </span>
                      <span className={`text-xs ${
                        item.status >= 200 && item.status < 300 ? 'text-green-600' :
                        item.status >= 400 ? 'text-red-600' : 'text-yellow-600'
                      }`}>
                        {item.status}
                      </span>
                    </div>
                    <div className="mt-1 text-gray-600 truncate">{item.url}</div>
                    <div className="mt-1 text-xs text-gray-500">{item.time}ms</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-6">
            {/* Request Builder */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold mb-4">Request</h2>
              
              {/* URL Input */}
              <div className="flex gap-2 mb-4">
                <select
                  value={request.method}
                  onChange={(e) => setRequest(prev => ({ ...prev, method: e.target.value }))}
                  className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {methods.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Enter request URL"
                  value={request.url}
                  onChange={(e) => setRequest(prev => ({ ...prev, url: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={sendRequest}
                  disabled={loading || !request.url}
                  className="flex items-center gap-2 px-6 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send size={16} />
                  )}
                  Send
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-4">
                <nav className="flex space-x-8">
                  {['headers', 'params', 'body'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === tab
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                  ))}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="bg-gray-50 rounded-lg p-4">
                {activeTab === 'headers' && (
                  <KeyValueEditor type="headers" title="Headers" />
                )}
                
                {activeTab === 'params' && (
                  <KeyValueEditor type="params" title="Query Parameters" />
                )}
                
                {activeTab === 'body' && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <label className="font-medium text-gray-700">Body Type:</label>
                      <select
                        value={request.bodyType}
                        onChange={(e) => setRequest(prev => ({ ...prev, bodyType: e.target.value }))}
                        className="px-3 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {bodyTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <textarea
                      placeholder={`Enter ${request.bodyType} data...`}
                      value={request.body}
                      onChange={(e) => setRequest(prev => ({ ...prev, body: e.target.value }))}
                      className="w-full h-40 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Response */}
            {response && (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">Response</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={copyResponse}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                    >
                      <Copy size={14} /> Copy
                    </button>
                  </div>
                </div>

                {response.error ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-800 mb-2">
                      <XCircle size={20} />
                      <span className="font-semibold">Error</span>
                    </div>
                    <p className="text-red-700">{response.error}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Status Info */}
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        {response.status >= 200 && response.status < 300 ? (
                          <CheckCircle className="text-green-500" size={20} />
                        ) : (
                          <XCircle className="text-red-500" size={20} />
                        )}
                        <span className={`font-semibold ${
                          response.status >= 200 && response.status < 300 ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {response.status} {response.statusText}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Clock size={16} />
                        <span>{response.time}ms</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Info size={16} />
                        <span>{response.size} bytes</span>
                      </div>
                    </div>

                    {/* Response Headers */}
                    <div>
                      <h3 className="font-medium text-gray-700 mb-2">Response Headers</h3>
                      <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                        <pre className="text-sm text-gray-600">
                          {JSON.stringify(response.headers, null, 2)}
                        </pre>
                      </div>
                    </div>

                    {/* Response Body */}
                    <div>
                      <h3 className="font-medium text-gray-700 mb-2">Response Body</h3>
                      <div className="bg-gray-900 text-green-400 rounded-lg p-4 max-h-96 overflow-auto">
                        <pre className="text-sm">
                          {typeof response.body === 'object' 
                            ? JSON.stringify(response.body, null, 2)
                            : response.body}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
 
    </div>
  );
};

export default ApiDev;